import * as React from 'react';
import { useIMask, ReactMaskOpts } from 'react-imask';
import './styles.scss';

interface MaskedInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The mask to apply to the input.
   * 0 for numbers, a for letters, * for any character.
   * Eg: '0000-0000-0000-0000'
   */
  mask: string;
  label?: string;
  helperText?: string;
  /** Options to pass to the react-imask.
   * See https://imask.js.org/guide.html#masked-input for more info
   */
  maskOptions?: ReactMaskOpts;
}

export const MaskedInput: React.FC<MaskedInputProps> = ({ mask, ...inputProps }) => {
  const { value, onChange, label, helperText, maskOptions: maskOpts = {}, ...rest } = inputProps;

  // Guards the programmatic sync effect below from re-notifying the parent.
  const suppressAccept = React.useRef(false);

  const {
    ref,
    value: maskedValue,
    setValue: setMaskedValue,
  } = useIMask(
    {
      mask,
      ...(maskOpts as any),
    },
    {
      // Native onChange on the <input> is unreliable here - IMask can rewrite
      // the DOM value (e.g. inserting spaces on paste) in a way that fools
      // React's change tracker into never firing it. onAccept always fires.
      onAccept: (acceptedValue) => {
        setMaskedValue(acceptedValue);
        if (suppressAccept.current) {
          return;
        }
        onChange?.({ target: { value: acceptedValue } } as React.ChangeEvent<HTMLInputElement>);
      },
    },
  );

  // Resync the mask's display whenever the parent's value changes externally
  // (mount, a different record, a cancel/revert) - a no-op after our own
  // onAccept, since maskedValue already matches by then.
  React.useEffect(() => {
    if (value !== maskedValue) {
      suppressAccept.current = true;
      setMaskedValue((value as string) ?? '');
      suppressAccept.current = false;
    }
  }, [value]);

  return (
    <div className="mask-input">
      {label !== undefined && (
        <label className="sub-heading" htmlFor={rest.id}>
          {label}
        </label>
      )}
      {/* no-op onChange: onAccept notifies the parent instead, this just silences React's controlled-input warning */}
      <input ref={ref as any} onChange={() => {}} value={maskedValue} {...rest} />
      {helperText && <span className="helper-text">{helperText}</span>}
    </div>
  );
};
